// src/modules/accounting/pages/AccountingDashboard.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Spin, Typography, message, Divider,
  Progress, DatePicker, Button, Radio, Space, Tooltip, Tag, Alert, Empty, Result,
} from 'antd';
import {
  DollarOutlined, BankOutlined, RiseOutlined, FallOutlined,
  TeamOutlined, ShopOutlined, SoundOutlined,
  ReloadOutlined, CheckCircleOutlined, WarningOutlined,
  BookOutlined, AccountBookOutlined, CalendarOutlined, LockOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { getAccountingDashboard, getEnhancedDashboard } from '../api';
import { fetchGstLedgerSummaries } from '../../smelter/api';
import { usePermissions } from '../../../hooks/usePermissions';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const pct = (v) => (v != null && v !== 0 ? `${Number(v).toFixed(1)}%` : '—');

/* ── Date Presets ── */
const PRESETS = [
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'This Quarter', value: 'quarter' },
  { label: 'This FY', value: 'fy' },
  { label: 'All Time', value: 'all' },
  { label: 'Custom', value: 'custom' },
];

function resolveDates(preset, customRange) {
  const now = dayjs();
  switch (preset) {
    case 'today':
      return [now.startOf('day'), now.endOf('day')];
    case 'week':
      return [now.startOf('week'), now.endOf('day')];
    case 'month':
      return [now.startOf('month'), now.endOf('day')];
    case 'quarter': {
      const qStart = now.subtract(3, 'month').startOf('month');
      return [qStart, now.endOf('day')];
    }
    case 'fy': {
      // Indian FY: April 1 → March 31
      const fyStart = now.month() < 3 // Jan-Mar = previous FY
        ? dayjs(`${now.year() - 1}-04-01`)
        : dayjs(`${now.year()}-04-01`);
      return [fyStart.startOf('day'), now.endOf('day')];
    }
    case 'custom':
      return customRange && customRange[0] && customRange[1]
        ? [customRange[0].startOf('day'), customRange[1].endOf('day')]
        : [null, null];
    case 'all':
    default:
      return [null, null];
  }
}

export default function AccountingDashboard() {
  const [data, setData] = useState(null);
  const [deData, setDeData] = useState(null); // double-entry data
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState('month');
  const [customRange, setCustomRange] = useState(null);
  const [gstTotals, setGstTotals] = useState(null); // smelter GST recovery summary
  const navigate = useNavigate();
  const { canViewModule } = usePermissions();
  const canViewAccountingDashboard = canViewModule('accounting');
  const canViewSmelter = canViewModule('smelter');

  useEffect(() => {
    if (!canViewSmelter) return;
    fetchGstLedgerSummaries()
      .then((r) => setGstTotals(r.totals || null))
      .catch(() => setGstTotals(null));
  }, [canViewSmelter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [start, end] = resolveDates(preset, customRange);
      const params = {};
      if (start) params.startDate = start.toISOString();
      if (end) params.endDate = end.toISOString();

      const deParams = {};
      if (start) { deParams.periodStart = start.toISOString(); deParams.asOfDate = (end || dayjs()).toISOString(); }
      if (end) deParams.periodEnd = end.toISOString();

      const [fundRes, deRes] = await Promise.all([
        getAccountingDashboard(params),
        getEnhancedDashboard(deParams).catch(() => null),
      ]);

      setData(fundRes.data || fundRes);
      if (deRes) setDeData(deRes.data || deRes);
    } catch {
      message.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [preset, customRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePresetChange = (e) => {
    const val = e.target.value;
    setPreset(val);
    if (val !== 'custom') setCustomRange(null);
  };

  // Permission check: show access denied if user doesn't have permission
  if (!canViewAccountingDashboard) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Result
            status="403"
            title="Access Denied"
            subTitle="You don't have permission to view the Accounting Dashboard."
            icon={<LockOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />}
          />
        </Card>
      </div>
    );
  }

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '60px auto' }} />;
  if (!data) return <Empty description="No data available" />;

  const {
    companyFunds = {},
    cashFlow = {},
    fundContributions: fund = {},
    revenue: rev = {},
    expenses: exp = {},
    profitAndLoss: pnl = {},
  } = data;

  const systemHealth = deData?.systemHealth;
  const financialSummary = deData?.financialSummary;
  const cashPosition = deData?.cashPosition;
  const journalCounts = deData?.journalCounts;

  /* ── date label for display ── */
  const [resolvedStart, resolvedEnd] = resolveDates(preset, customRange);
  const dateLabel = preset === 'all'
    ? 'All Time'
    : resolvedStart && resolvedEnd
      ? `${resolvedStart.format('DD MMM YYYY')} — ${resolvedEnd.format('DD MMM YYYY')}`
      : '';

  const categoryLabels = {
    SALARY: 'Salary', RENT: 'Rent', ELECTRICITY: 'Electricity',
    INTERNET_TELECOM: 'Internet & Telecom', OFFICE_SUPPLIES: 'Office Supplies',
    MARKETING_ONLINE: 'Marketing (Online)', MARKETING_OFFLINE: 'Marketing (Offline)',
    TRAVEL: 'Travel', INSURANCE: 'Insurance', LEGAL_COMPLIANCE: 'Legal & Compliance',
    GOLD_TESTING: 'Gold Testing', PACKAGING_LOGISTICS: 'Packaging & Logistics',
    SECURITY: 'Security', MAINTENANCE: 'Maintenance',
    SOFTWARE_SUBSCRIPTIONS: 'Software / SaaS', BANK_CHARGES: 'Bank Charges',
    TAXES_DUTIES: 'Taxes & Duties', DEPRECIATION: 'Depreciation',
    MISCELLANEOUS: 'Miscellaneous',
  };

  const categoryColors = {
    SALARY: '#1677ff', RENT: '#722ed1', ELECTRICITY: '#faad14',
    MARKETING_ONLINE: '#52c41a', MARKETING_OFFLINE: '#13c2c2',
    GOLD_TESTING: '#eb2f96', MISCELLANEOUS: '#8c8c8c',
  };

  return (
    <div>
      {/* ════════════════════════════════════════════
       *  DATE FILTER BAR
       * ════════════════════════════════════════════ */}
      <Card size="small" style={{ marginBottom: 20 }} bodyStyle={{ padding: '12px 16px' }}>
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col xs={24} lg={16}>
            <Space wrap size="small">
              <CalendarOutlined style={{ color: '#1677ff' }} />
              <Radio.Group
                value={preset}
                onChange={handlePresetChange}
                optionType="button"
                buttonStyle="solid"
                size="small"
              >
                {PRESETS.map((p) => (
                  <Radio.Button key={p.value} value={p.value}>{p.label}</Radio.Button>
                ))}
              </Radio.Group>
              {preset === 'custom' && (
                <RangePicker
                  value={customRange}
                  onChange={setCustomRange}
                  format="DD/MM/YYYY"
                  size="small"
                  allowClear={false}
                />
              )}
            </Space>
          </Col>
          <Col>
            <Space>
              {dateLabel && <Text type="secondary" style={{ fontSize: 12 }}>{dateLabel}</Text>}
              <Tooltip title="Refresh">
                <Button icon={<ReloadOutlined />} size="small" onClick={fetchData} />
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ════════════════════════════════════════════
       *  SYSTEM HEALTH BAR (from Double-Entry)
       * ════════════════════════════════════════════ */}
      {systemHealth && (
        <Card
          size="small"
          style={{
            marginBottom: 20,
            background: systemHealth.status === 'HEALTHY' ? '#f6ffed' : '#fff7e6',
            borderColor: systemHealth.status === 'HEALTHY' ? '#b7eb8f' : '#ffd591',
          }}
          bodyStyle={{ padding: '10px 16px' }}
        >
          <Row gutter={16} align="middle">
            <Col>
              {systemHealth.status === 'HEALTHY'
                ? <CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                : <WarningOutlined style={{ fontSize: 20, color: '#faad14' }} />}
            </Col>
            <Col flex="auto">
              <Text strong style={{ fontSize: 13 }}>
                Accounting System: {systemHealth.status === 'HEALTHY' ? 'All checks passed' : 'Needs attention'}
              </Text>
              {systemHealth.accountingEquation?.equation && (
                <Text style={{ marginLeft: 12, fontSize: 12 }} code>{systemHealth.accountingEquation.equation}</Text>
              )}
            </Col>
            <Col>
              <Space split={<Divider type="vertical" />}>
                <Tag color={systemHealth.trialBalance?.isBalanced ? 'green' : 'red'}>
                  TB {systemHealth.trialBalance?.isBalanced ? '✓' : '✗'}
                </Tag>
                <Tag color={systemHealth.accountingEquation?.isBalanced ? 'green' : 'red'}>
                  A=L+E {systemHealth.accountingEquation?.isBalanced ? '✓' : '✗'}
                </Tag>
                {journalCounts && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <BookOutlined /> {journalCounts.POSTED || 0} posted / {journalCounts.total || 0} total
                  </Text>
                )}
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {/* ════════════════════════════════════════════
       *  TOP-LINE KPIs
       * ════════════════════════════════════════════ */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small" bordered hoverable>
            <Statistic
              title="Total Fund Balance"
              value={fmt(companyFunds.totalBalance || 0)}
              prefix={<BankOutlined />}
              valueStyle={{ fontSize: 16, fontWeight: 600, color: (companyFunds.totalBalance || 0) >= 0 ? '#3f8600' : '#cf1322' }}
            />
            <Text type="secondary" style={{ fontSize: 10 }}>
              {companyFunds.fundCount || 0} fund{(companyFunds.fundCount || 0) !== 1 ? 's' : ''}
            </Text>
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small" bordered hoverable>
            <Statistic
              title="Net Cash Flow"
              value={fmt(cashFlow.netCashFlow || 0)}
              prefix={cashFlow.netCashFlow >= 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{ fontSize: 16, fontWeight: 600, color: (cashFlow.netCashFlow || 0) >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small" bordered hoverable>
            <Statistic title="Cash Inflow" value={fmt(cashFlow.totalInflow || 0)} prefix={<RiseOutlined />}
              valueStyle={{ fontSize: 16, color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small" bordered hoverable>
            <Statistic title="Cash Outflow" value={fmt(cashFlow.totalOutflow || 0)} prefix={<FallOutlined />}
              valueStyle={{ fontSize: 16, color: '#cf1322' }} />
          </Card>
        </Col>
        {/* Double-entry cash position */}
        {cashPosition && (
          <Col xs={12} sm={8} lg={4}>
            <Card size="small" bordered hoverable style={{ borderLeft: '3px solid #1677ff' }}>
              <Statistic title="Ledger Cash & Bank" value={fmt(cashPosition.total)} prefix={<AccountBookOutlined />}
                valueStyle={{ fontSize: 16, fontWeight: 600, color: '#1677ff' }} />
              <Text type="secondary" style={{ fontSize: 10 }}>From journal entries</Text>
            </Card>
          </Col>
        )}
        <Col xs={12} sm={8} lg={4}>
          <Card size="small" bordered hoverable>
            <Statistic title="Net Profit" value={fmt(pnl.netProfit || 0)}
              prefix={(pnl.netProfit || 0) >= 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{ fontSize: 16, fontWeight: 600, color: (pnl.netProfit || 0) >= 0 ? '#3f8600' : '#cf1322' }} />
            <Text type="secondary" style={{ fontSize: 10 }}>Margin: {pnl.netProfitMargin || 0}%</Text>
          </Card>
        </Col>
      </Row>

      {/* ════════════════════════════════════════════
       *  FUND INTEGRATION PROGRESS
       * ════════════════════════════════════════════ */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12}>
          <Card size="small" title="Revenue Integration" bordered>
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="circle"
                percent={parseFloat(cashFlow.integrationCoverage?.revenueIntegrated?.percentage || 0)}
                format={(percent) => `${percent}%`}
                strokeColor="#52c41a"
                size={80}
              />
              <div style={{ marginTop: 8 }}>
                <Text strong>{fmt(cashFlow.integrationCoverage?.revenueIntegrated?.total || 0)}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {cashFlow.integrationCoverage?.revenueIntegrated?.count || 0} transactions integrated
                </Text>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card size="small" title="Expense Integration" bordered>
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="circle"
                percent={parseFloat(cashFlow.integrationCoverage?.expensesIntegrated?.percentage || 0)}
                format={(percent) => `${percent}%`}
                strokeColor="#1677ff"
                size={80}
              />
              <div style={{ marginTop: 8 }}>
                <Text strong>{fmt(cashFlow.integrationCoverage?.expensesIntegrated?.total || 0)}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {cashFlow.integrationCoverage?.expensesIntegrated?.count || 0} payments from funds
                </Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ════════════════════════════════════════════
       *  INDIVIDUAL FUND BALANCES
       * ════════════════════════════════════════════ */}
      {companyFunds.funds && companyFunds.funds.length > 0 && (
        <Card size="small" title="Individual Fund Balances" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            {companyFunds.funds.map((fundItem) => (
              <Col xs={12} sm={8} md={6} key={fundItem.id}>
                <Card size="small" bordered style={{ textAlign: 'center' }}>
                  <Statistic
                    title={fundItem.name}
                    value={fmt(fundItem.balance)}
                    valueStyle={{ fontSize: 14, color: fundItem.balance >= 0 ? '#3f8600' : '#cf1322' }}
                  />
                  {fundItem.description && (
                    <Text type="secondary" style={{ fontSize: 10 }}>{fundItem.description}</Text>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* ════════════════════════════════════════════
       *  REVENUE & PROFIT ANALYSIS
       * ════════════════════════════════════════════ */}
      <Title level={5} style={{ marginBottom: 12 }}>Revenue & Profit Analysis</Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small" bordered hoverable>
            <Statistic title="Smelter Revenue" value={fmt(rev.smelterRevenue || 0)} prefix={<DollarOutlined />}
              valueStyle={{ fontSize: 16, fontWeight: 600 }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered hoverable>
            <Statistic title="Gold Purchase Cost" value={fmt(rev.goldPurchaseCost || 0)} prefix={<ShopOutlined />}
              valueStyle={{ fontSize: 16 }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered hoverable>
            <Statistic title="Gross Profit" value={fmt(rev.grossProfit || 0)} prefix={<RiseOutlined />}
              valueStyle={{ fontSize: 16, fontWeight: 600, color: (rev.grossProfit || 0) >= 0 ? '#3f8600' : '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered hoverable>
            <Statistic title="Dispatch Count" value={rev.dispatchCount || 0} suffix="dispatches"
              valueStyle={{ fontSize: 16 }} />
          </Card>
        </Col>
        {canViewSmelter && gstTotals && (
          <>
            <Col xs={12} sm={6}>
              <Tooltip title="GST to deposit with the government (unfiled). Click for the GST recovery ledger.">
                <Card size="small" bordered hoverable onClick={() => navigate('/smelters/gst-ledger')} style={{ cursor: 'pointer' }}>
                  <Statistic title="GST Unfiled (to deposit)" value={fmt(gstTotals.unfiled || 0)} prefix={<AccountBookOutlined />}
                    valueStyle={{ fontSize: 16, color: (gstTotals.unfiled || 0) > 0 ? '#d48806' : undefined }} />
                </Card>
              </Tooltip>
            </Col>
            <Col xs={12} sm={6}>
              <Tooltip title="GST recoverable from smelters (charged − returned). Click for the GST recovery ledger.">
                <Card size="small" bordered hoverable onClick={() => navigate('/smelters/gst-ledger')} style={{ cursor: 'pointer' }}>
                  <Statistic title="GST to Recover (Smelters)" value={fmt(gstTotals.outstanding || 0)} prefix={<BankOutlined />}
                    valueStyle={{ fontSize: 16, color: (gstTotals.outstanding || 0) > 0 ? '#cf1322' : '#3f8600' }} />
                </Card>
              </Tooltip>
            </Col>
          </>
        )}
      </Row>

      {/* ════════════════════════════════════════════
       *  DOUBLE-ENTRY P&L COMPARISON
       *  (show alongside the fund-based P&L for cross-reference)
       * ════════════════════════════════════════════ */}
      {financialSummary && (
        <>
          <Title level={5} style={{ marginBottom: 12 }}>
            <AccountBookOutlined style={{ marginRight: 6 }} />
            Double-Entry Financial Summary
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered style={{ borderTop: '3px solid #52c41a' }}>
                <Statistic title="Revenue (Ledger)" value={fmt(financialSummary.profitLoss?.totalRevenue)}
                  valueStyle={{ fontSize: 15, color: '#3f8600' }} />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered style={{ borderTop: '3px solid #ff4d4f' }}>
                <Statistic title="Expenses (Ledger)" value={fmt(financialSummary.profitLoss?.totalExpenses)}
                  valueStyle={{ fontSize: 15, color: '#cf1322' }} />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered style={{ borderTop: '3px solid #1677ff' }}>
                <Statistic title="Gross Profit" value={fmt(financialSummary.profitLoss?.grossProfit)}
                  valueStyle={{ fontSize: 15 }}
                  suffix={<Text type="secondary" style={{ fontSize: 10 }}>{pct(financialSummary.profitLoss?.grossProfitMargin)}</Text>} />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered style={{ borderTop: '3px solid #722ed1' }}>
                <Statistic title="Operating Profit" value={fmt(financialSummary.profitLoss?.operatingProfit)}
                  valueStyle={{ fontSize: 15 }}
                  suffix={<Text type="secondary" style={{ fontSize: 10 }}>{pct(financialSummary.profitLoss?.operatingProfitMargin)}</Text>} />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered style={{ borderTop: '3px solid #13c2c2' }}>
                <Statistic title="Net Profit (Ledger)" value={fmt(financialSummary.profitLoss?.netProfit)}
                  valueStyle={{ fontSize: 15, fontWeight: 600, color: (financialSummary.profitLoss?.netProfit || 0) >= 0 ? '#3f8600' : '#cf1322' }}
                  suffix={<Text type="secondary" style={{ fontSize: 10 }}>{pct(financialSummary.profitLoss?.netProfitMargin)}</Text>} />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered style={{ borderTop: '3px solid #fa8c16' }}>
                <Statistic title="Total Assets" value={fmt(financialSummary.balanceSheet?.totalAssets)}
                  valueStyle={{ fontSize: 15 }} />
                <Text type="secondary" style={{ fontSize: 10 }}>
                  WC: {fmt(financialSummary.balanceSheet?.workingCapital)} · CR: {financialSummary.balanceSheet?.currentRatio?.toFixed(2) || '—'}
                </Text>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* ════════════════════════════════════════════
       *  FUND CONTRIBUTIONS
       * ════════════════════════════════════════════ */}
      <Title level={5} style={{ marginBottom: 12 }}>Fund Contributions (Advance Payments)</Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small" bordered hoverable>
            <Statistic title="Total Contributed" value={fmt(fund.totalContributed || 0)} prefix={<BankOutlined />}
              valueStyle={{ fontSize: 16, fontWeight: 600 }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered hoverable>
            <Statistic title="Settled (Recovered)" value={fmt(fund.totalSettled || 0)}
              valueStyle={{ fontSize: 16, color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered hoverable>
            <Statistic title="Outstanding" value={fmt(fund.totalOutstanding || 0)}
              valueStyle={{ fontSize: 16, color: (fund.totalOutstanding || 0) > 0 ? '#cf1322' : '#3f8600', fontWeight: 600 }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered hoverable>
            <Statistic title="Contributions" value={fund.count || 0} suffix="records" valueStyle={{ fontSize: 16 }} />
          </Card>
        </Col>
      </Row>

      {/* ════════════════════════════════════════════
       *  EXPENSE SUMMARY
       * ════════════════════════════════════════════ */}
      <Title level={5} style={{ marginBottom: 12 }}>Expense Summary</Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small" bordered hoverable>
            <Statistic title="Total Expenses" value={fmt(exp.total || 0)} prefix={<FallOutlined />}
              valueStyle={{ fontSize: 16, fontWeight: 600, color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered hoverable>
            <Statistic title="Salary" value={fmt(exp.salary || 0)} prefix={<TeamOutlined />}
              valueStyle={{ fontSize: 16 }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered hoverable>
            <Statistic title="Paid from Funds" value={fmt(exp.paidFromFunds || 0)} prefix={<BankOutlined />}
              valueStyle={{ fontSize: 16, color: '#1677ff' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered hoverable>
            <Statistic title="Marketing" value={fmt(exp.marketing || 0)} prefix={<SoundOutlined />}
              valueStyle={{ fontSize: 16 }} />
            <Text type="secondary" style={{ fontSize: 10 }}>
              Online: {fmt(exp.marketingOnline || 0)} · Offline: {fmt(exp.marketingOffline || 0)}
            </Text>
          </Card>
        </Col>
      </Row>

      {/* ── Category-wise breakdown ── */}
      <Card size="small" title="Expense Breakdown by Category" style={{ marginBottom: 24 }}>
        {Object.entries(exp.byCategory || {}).length > 0 ? (
          Object.entries(exp.byCategory || {})
            .sort(([, a], [, b]) => (b.total || 0) - (a.total || 0))
            .map(([cat, info]) => {
              const catPct = exp.total ? ((info.total / exp.total) * 100).toFixed(1) : 0;
              return (
                <div key={cat} style={{ marginBottom: 10 }}>
                  <Row justify="space-between" style={{ marginBottom: 2 }}>
                    <Col><Text style={{ fontSize: 13 }}>{categoryLabels[cat] || cat}</Text></Col>
                    <Col>
                      <Text strong style={{ fontSize: 13 }}>{fmt(info.total)}</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}> ({catPct}%) </Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>· {info.count} txn{info.count !== 1 ? 's' : ''}</Text>
                    </Col>
                  </Row>
                  <Progress percent={Number(catPct)} showInfo={false} strokeColor={categoryColors[cat] || '#1677ff'} size="small" />
                </div>
              );
            })
        ) : (
          <Text type="secondary">No expenses recorded for this period</Text>
        )}
      </Card>

      {/* ════════════════════════════════════════════
       *  CASH FLOW BY SOURCE
       * ════════════════════════════════════════════ */}
      {cashFlow.flowBySource && Object.keys(cashFlow.flowBySource).length > 0 && (
        <Card size="small" title="Cash Flow by Source" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            {Object.entries(cashFlow.flowBySource).map(([source, flow]) => (
              <Col xs={12} sm={8} md={6} key={source}>
                <Card size="small" bordered style={{ textAlign: 'center' }}>
                  <Text strong style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                    {source.replace(/_/g, ' ')}
                  </Text>
                  {flow.inflow > 0 && (
                    <div><Text style={{ fontSize: 11, color: '#3f8600' }}>↗ In: {fmt(flow.inflow)}</Text></div>
                  )}
                  {flow.outflow > 0 && (
                    <div><Text style={{ fontSize: 11, color: '#cf1322' }}>↘ Out: {fmt(flow.outflow)}</Text></div>
                  )}
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    {flow.count} txn{flow.count !== 1 ? 's' : ''}
                  </Text>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* ════════════════════════════════════════════
       *  P&L STATEMENT (Fund-Based)
       * ════════════════════════════════════════════ */}
      <Card size="small" title="Profit & Loss Statement (Fund-Based)" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 8]}>
          <Col span={16}><Text>Smelter Revenue (Gold Sales)</Text></Col>
          <Col span={8} style={{ textAlign: 'right' }}><Text strong>{fmt(rev.smelterRevenue)}</Text></Col>

          <Col span={16}><Text>Less: Gold Purchase Cost</Text></Col>
          <Col span={8} style={{ textAlign: 'right' }}><Text type="danger">({fmt(rev.goldPurchaseCost)})</Text></Col>

          <Col span={24}><Divider style={{ margin: '8px 0' }} /></Col>

          <Col span={16}><Text strong>Gross Profit</Text></Col>
          <Col span={8} style={{ textAlign: 'right' }}>
            <Text strong style={{ color: rev.grossProfit >= 0 ? '#3f8600' : '#cf1322' }}>{fmt(rev.grossProfit)}</Text>
          </Col>

          <Col span={24}><Divider style={{ margin: '8px 0' }} /></Col>

          <Col span={16}><Text>Less: Operating Expenses</Text></Col>
          <Col span={8} style={{ textAlign: 'right' }}><Text type="danger">({fmt(exp.total)})</Text></Col>

          {exp.salary > 0 && <>
            <Col span={16} style={{ paddingLeft: 24 }}><Text type="secondary">Salaries</Text></Col>
            <Col span={8} style={{ textAlign: 'right' }}><Text type="secondary">{fmt(exp.salary)}</Text></Col>
          </>}
          {exp.rent > 0 && <>
            <Col span={16} style={{ paddingLeft: 24 }}><Text type="secondary">Rent</Text></Col>
            <Col span={8} style={{ textAlign: 'right' }}><Text type="secondary">{fmt(exp.rent)}</Text></Col>
          </>}
          {exp.electricity > 0 && <>
            <Col span={16} style={{ paddingLeft: 24 }}><Text type="secondary">Electricity</Text></Col>
            <Col span={8} style={{ textAlign: 'right' }}><Text type="secondary">{fmt(exp.electricity)}</Text></Col>
          </>}
          {exp.marketing > 0 && <>
            <Col span={16} style={{ paddingLeft: 24 }}><Text type="secondary">Marketing</Text></Col>
            <Col span={8} style={{ textAlign: 'right' }}><Text type="secondary">{fmt(exp.marketing)}</Text></Col>
          </>}

          {(pnl.agentCommissions?.total || 0) > 0 && <>
            <Col span={24}><Divider style={{ margin: '8px 0' }} /></Col>
            <Col span={16}><Text>Less: Agent Commissions</Text></Col>
            <Col span={8} style={{ textAlign: 'right' }}><Text type="danger">({fmt(pnl.agentCommissions.total)})</Text></Col>
            <Col span={16} style={{ paddingLeft: 24 }}><Text type="secondary">Paid</Text></Col>
            <Col span={8} style={{ textAlign: 'right' }}><Text type="secondary">{fmt(pnl.agentCommissions.paid || 0)}</Text></Col>
            <Col span={16} style={{ paddingLeft: 24 }}><Text type="secondary">Pending</Text></Col>
            <Col span={8} style={{ textAlign: 'right' }}><Text type="secondary">{fmt(pnl.agentCommissions.pending || 0)}</Text></Col>
          </>}

          <Col span={24}><Divider style={{ margin: '8px 0' }} /></Col>

          <Col span={16}><Title level={5} style={{ margin: 0 }}>Net Profit / (Loss)</Title></Col>
          <Col span={8} style={{ textAlign: 'right' }}>
            <Title level={5} style={{ margin: 0, color: pnl.netProfit >= 0 ? '#3f8600' : '#cf1322' }}>
              {fmt(pnl.netProfit)}
            </Title>
          </Col>
        </Row>
      </Card>
    </div>
  );
}
